import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as pako from 'pako';
import { NEVER, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, tap, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth/auth.service';
import { StorageAccountService } from './services/storage-account.service';
import { DestinationInfo } from './types/destinationInfo';
import { LocationInfo } from './types/locationInfo';
import { LocationName } from './types/locationName';
import { LocationProfile } from './types/locationProfile';
import { LocationRequestPayload } from './types/locationRequestPayload';
import { LocationTrailer } from './types/locationTrailer';
import { LocationUser } from './types/locationUser';
import { LocationUserImportResponse } from './types/locationUserImportResponse';
import { LocationUsersExportPayload } from './types/locationUsersExportPayload';
import { NotificationDeliveryType } from './types/NotificationDeliveryType';
import { NotificationUserLocationSettings } from './types/NotificationUserLocationSettings';
import { NotificationSettingsUpdateType, NotificationUserLocationSettingsUpdates } from './types/NotificationUserLocationSettingsUpdates';
import { OnPropertyData } from './types/onPropertyData';
import { SaveRequestResponse } from './types/saveRequestResponse';
import { TrailerRequestAudit } from './types/trailerRequestAudit';
import { TrailerRequestCommentsFilter } from './types/trailerRequestCommentsFilter';
import { TrailerRequestCommentsResponse } from './types/trailerRequestCommentsResponse';
import { TrailerRequestDataResponse } from './types/trailerRequestDataResponse';
import { TrailerRequestExportPayload } from './types/trailerRequestExportPayload';
import { TrailerRequestPayload } from './types/trailerRequestPayload';
import { TrailerRequestResponse } from './types/trailerRequestResponse';
import { TrailerTypeInfo } from './types/trailerTypeInfo';
import { User } from './types/user';
import { UserAccessHistory } from './types/userAccessHistory';
import { UserApiLoginMetrics } from './types/userApiLoginMetrics';
import { UserWithLocations } from './types/userWithLocations';
import { Auth0WrapperService } from './auth/auth0-wrapper.service';


@Injectable({
  providedIn: 'root'
})
export class ApiService implements OnDestroy {
  disconnect$: Subject<boolean> = new Subject<boolean>();
  refreshedToken$: Subject<boolean> = new Subject<boolean>();
  private auth0Service: Auth0Service

  constructor(
    public jwtHelper: JwtHelperService, 
    private httpClient: HttpClient, 
    private router: Router, 
    private authService: AuthService,    
    private auth0WrapperService: Auth0WrapperService,
    private storageAccountService: StorageAccountService
  ) {
    this.auth0Service = this.auth0WrapperService.getInstance();
  }

  ngOnDestroy() {
    this.disconnect$.next(true);
    this.disconnect$.unsubscribe();
  }

  login(payload): Observable<User> {
    return this.post<User>(`user/authenticate`, payload, true);
  }

  getOAuthRedirect(): Observable<Response> {
    return this.httpClient.get('api/user/oauth/redirect', { responseType: 'text' as 'text', observe: 'response' as any }) as any;
  }

  authorize(code: string, state: string): Observable<User> {
    return this.get<User>(`user/oauth/authorize?code=${code}&state=${state}`, true);
  }

  logMetrics(metrics: UserApiLoginMetrics) {
    return this.post(`user/metrics`, metrics);
  }

  getAllRequests(payload: TrailerRequestPayload): Observable<TrailerRequestDataResponse> {
    return this.post<TrailerRequestDataResponse>(`TrailerRequest/all`, payload);
  }

  exportRequests(filename: string, payload: TrailerRequestExportPayload): Observable<void> {
    return this.download(filename, `TrailerRequest/export`, payload);
  }

  exportLocationUsers(filename: string, payload: LocationUsersExportPayload): Observable<void> {
    return this.download(filename, `Location/export`, payload);
  }

  downloadUserImportTemplate(filename: string, language: string): Observable<void> {
    return this.download(filename, `Location/importTemplate?language=${language}`, null);
  }

  getTrailerRequestUpdates(requests: TrailerRequestResponse[]): Observable<TrailerRequestResponse[]> {
    return this.post<TrailerRequestResponse[]>(`TrailerRequest/updates`, requests.map(r => ({ requestId: r.requestId, lastTimestamp: r._ts })));
  }

  getTrailerRequest(requestId: string): Observable<TrailerRequestResponse | null> {
    return this.get<TrailerRequestResponse | null>(`TrailerRequest/${requestId}`);
  }

  getTrailerRequestAudit(requestId: string): Observable<TrailerRequestAudit | null> {
    return this.get<TrailerRequestAudit | null>(`TrailerRequest/${requestId}/audit`);
  }

  getRequestComments(payload: TrailerRequestCommentsFilter): Observable<TrailerRequestCommentsResponse> {
    return this.post<TrailerRequestCommentsResponse>(`TrailerRequest/comments`, payload);
  }

  getUnreadCommentDates(): Observable<Date[]> {
    return this.get<Date[]>('TrailerRequest/commentDates');
  }

  getLocationNames(term: string): Observable<LocationName[]> {
    return this.get<LocationName[]>(`Location/search?term=${term}`);
  }

  getLocationNamesWithAddress(term: string): Observable<LocationName[]> {
    return this.post<LocationName[]>(`Location/searchWithAddress`, { term });
  }

  getAuthorizedLocationNames(term: string, userId: string): Observable<LocationName[]> {
    return this.get<LocationName[]>(`Location/auth?term=${term}&userId=${userId}`);
  }

  searchLocations(term: string, payload: LocationRequestPayload): Observable<LocationInfo[]> {
    return this.post<LocationInfo[]>(`Location/search?term=${term}`, payload);
  }

  getTrailerTypes(slic: string): Observable<LocationTrailer[]> {
    return this.get<LocationTrailer[]>(`TrailerRequest/trailerTypes/${slic}`);
  }

  getTrailerInfo(trailerType: string): Observable<TrailerTypeInfo> {
    return this.get<TrailerTypeInfo>(`TrailerRequest/trailerInfo/${trailerType}`);
  }

  getDestinationInfo(slic: string): Observable<DestinationInfo[]> {
    return this.get<DestinationInfo[]>(`TrailerRequest/destinationInfo/${slic}`);
  }

  createRequest(request: TrailerRequestResponse): Observable<SaveRequestResponse> {
    return this.post<SaveRequestResponse>(`TrailerRequest/create/?userDateTime=${new Date().toUTCString()}`, request);
  }

  editRequest(request: TrailerRequestResponse, adjustForBrowserTime: boolean = true): Observable<SaveRequestResponse> {
    return this.post<SaveRequestResponse>(`TrailerRequest/?userDateTime=${new Date().toUTCString()}`, request);
  }

  cancelRequest(request: TrailerRequestResponse) {
    return this.post(`TrailerRequest/cancel`, request);
  }

  getComment(requestId: string): Observable<TrailerRequestResponse | null> {
    return this.get<TrailerRequestResponse | null>(`TrailerRequest/${requestId}`);
  }

  addComment(request: TrailerRequestResponse): Observable<SaveRequestResponse> {
    return this.post<SaveRequestResponse>(`TrailerRequest`, request);
  }

  getLocations() {
    return this.get<LocationProfile[]>(`Location/list`);
  }

  updateCustomName(location: LocationProfile) {
    return this.post(`Location/customName`, location);
  }

  getLocationBySlicAbbrev(slic: string) {
    return this.get<LocationProfile>(`Location/${slic}`);
  }

  getLocation(country: string, slicNumber: string) {
    return this.get<LocationProfile>(`Location/${country}/${slicNumber}`);
  }

  getDefaultResponsibleSiteFromLocation(country: string, slicNumber: string) {
    return this.get<LocationProfile>(`Location/${country}/${slicNumber}/defaultResponsibleSite`);
  }

  getUsersWithLocations(locations: any[]) {
    return this.post<UserWithLocations[]>(`Location/users`, locations);
  }

  getLocationUsers(slicNumber: string, countryCode: string) {
    return this.get<LocationUser[]>(`Location/${countryCode}/${slicNumber}/users`);
  }

  getLocationUser(slic: string, userId: string) {
    return this.get<LocationUser>(`Location/${slic}/users/${userId}`);
  }

  saveUser(slic: string, user: LocationUser) {
    return this.post(`Location/${slic}/users/${user.userId}`, user);
  }

  removeUserFromAllLocations(userId: string): Observable<null> {
    return this.delete(`Location/users/${userId}`);
  }

  addUsers(slic: string, users: LocationUser[]) {
    return this.post(`Location/${slic}/users`, users);
  }

  addLocationUsers(locations: LocationProfile[]) {
    return this.post(`Location/addusers`, locations);
  }

  getOnPropertyDetailsForLocation(location: LocationProfile) {
    return this.get<OnPropertyData>(`OnProperty/${location.countryCode}/${location.slicNumber}/${location.slicName}`);
  }

  searchAllLocations(searchTerm: string) {
    return this.get<LocationName[]>(`Location/search-all?term=${searchTerm}`)
  }

  async refreshToken() {
    // With TmsAuth, token refresh is handled automatically by the lib
    // We just need to check if the user is authenticated
    const isAuthenticated = await this.authService.isAuthenticated$.pipe(take(1)).toPromise();
    
    if (!isAuthenticated) {
      console.log('User not authenticated. Redirecting to login.');
      this.authService.login({
        redirectPath: this.router.url
      });
      return;
    }
    
    // Signal that token has been refreshed for backward compatibility
    this.refreshedToken$.next(true);
    return;
  }

  private getAuthHeader(): Observable<HttpHeaders> {
    // Get token from TmsAuth service
    return this.authService.getAccessToken$().pipe(
      take(1),
      switchMap(token => {
        if (token) {
          return of(new HttpHeaders({ 'Authorization': `Bearer ${token}` }));
        } else {
          // For backward compatibility - check localStorage if token not available
          // This branch should rarely if ever be taken in the new system
          const localToken = localStorage.getItem('token') || "";
          if (localToken) {
            const compressed = pako.deflate(localToken);
            return new Observable<HttpHeaders>(sub => {
              const fr = new FileReader();
              fr.onload = () => {
                const result = fr.result as string;
                sub.next(new HttpHeaders({ 'Authorization': `Bearer ${result.split('base64,')[1]}` }));
                sub.complete();
              }
              fr.readAsDataURL(new Blob([compressed]));
            });
          } else {
            return of(new HttpHeaders());
          }
        }
      }),
      catchError(() => {
        console.error('Error getting auth token');
        return of(new HttpHeaders());
      })
    );
  }

  public checkIfTokenRefreshRequired(bypass: boolean): Observable<boolean> {
    if (bypass)
      return of(false);
    
    // With TmsAuth, token refresh is handled automatically
    // We just need to check if the user is authenticated
    return this.authService.isAuthenticated$.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          // User is not authenticated, redirect to gateway
          this.router.navigate(['gateway']);
          return NEVER;
        }
        
        // TmsAuth will handle token refresh automatically
        return of(false); 
      })
    );
  }

  public CheckMaintenance(): Observable<boolean> {
    return this.get<boolean>(`user/maintenance`);
  }

  public StorageAccountIsDown(): Observable<boolean> {
    return this.get<boolean>(`StorageAccount/StorageAccountIsDown`);
  }

  public getUserAccessHistory(userId: string): Observable<UserAccessHistory> {
    return this.get<UserAccessHistory>(`user/getUserAccessHistory/${userId}`);
  }

  public importUsers(importUserFile: File, locale: string): Observable<LocationUserImportResponse> {
    return this.postWithFile(`location/importLocationUsers/${locale}`, importUserFile);
  }

  public getNotificationDeliveryType(): Observable<NotificationDeliveryType> {
    return this.get<NotificationDeliveryType>(`notifications/deliveryType`);
  }

  public saveNotificationDeliveryType(deliveryTypeSettings: NotificationDeliveryType) {
    return this.post<NotificationDeliveryType>(`notifications/deliveryType`, deliveryTypeSettings);
  }

  public getNotificationLocationSettings(): Observable<NotificationUserLocationSettings[]> {
    return this.get<NotificationUserLocationSettings[]>(`notifications/settings`);
  }

  public saveNotificationLocationSettings(updates: NotificationUserLocationSettingsUpdates) {
    return this.post<NotificationUserLocationSettings>(`notifications/settings-type`, updates);
  }

  public toggleAllNotificationLocationSettings(updateType: NotificationSettingsUpdateType, enabled: boolean) {
    return this.post(`notifications/toggle-all`, { enabled, updateType});
  }

  public checkIfNotificationSettingsOutOfDate(payload) {
    return this.post<boolean>(`notifications/out-of-date`, payload);
  }

  public exportTrailers(fileName: string, payload) {
    return this.download(fileName, 'onProperty/export-trailers', payload);
  }

  private get<T>(endpoint: string, allowAnonymous: boolean = false): Observable<T> {
    return this.callWithAuth<T>(this.getFunc<T>(endpoint), allowAnonymous);
  }

  private getFunc<T>(endpoint: string): (headers: HttpHeaders) => Observable<T> {
    return (auth) => {
      return this.httpClient.get<T>(`/api/${endpoint}`, {
        headers: auth
      }).pipe(catchError(error => this.handleApiError(error)));
    }
  }

  private post<T>(endpoint: string, payload: any, allowAnonymous: boolean = false): Observable<T> {
    return this.callWithAuth<T>(this.postFunc<T>(endpoint, payload), allowAnonymous);
  }

  private postWithFile<T>(endpoint: string, fileToUpload: File, allowAnonymous: boolean = false): Observable<T> {
    return this.callWithAuth<T>(this.postWithFileFunc<T>(endpoint, fileToUpload), allowAnonymous);
  }

  private postWithFileFunc<T>(endpoint: string, fileToUpload: File): (headers: HttpHeaders) => Observable<T> {
    return (auth) => {
      const formData: FormData = new FormData();
      formData.append('uploadedFile', fileToUpload);
      return this.httpClient.post<T>(`/api/${endpoint}`, formData, {
        headers: auth
      }).pipe(catchError(error => this.handleApiError(error)));
    }
  }

  private handleApiError(error: any) {
    if (error.status === 401) {
      // Check authentication state before redirecting
      this.auth0Service.isAuthenticated$.pipe(take(1)).subscribe(isAuthenticated => {
        if (!isAuthenticated) {
          // Only redirect if user is not authenticated with Auth0
          this.router.navigate(['gateway']);
        } else {
          console.warn('Received 401 despite Auth0 authentication. Token may not be valid for this API.');
          // Try to refresh the token by logging in again if there seems to be a token issue
          this.auth0Service.loginWithRedirect({
            appState: { target: this.router.url }
          });
        }
      });
      return NEVER;
    } else if (error.status === 504) {
      localStorage.setItem("storageAccountDown", "true");
      this.storageAccountService.isDown.emit(true);
      return of(error);
    }

    return throwError(() => error);
  }

  private postFunc<T>(endpoint: string, payload: any): (headers: HttpHeaders) => Observable<T> {
    return (auth) => {
      return this.httpClient.post<T>(`/api/${endpoint}`, payload, {
        headers: auth
      }).pipe(catchError(error => this.handleApiError(error)));
    };
  }

  private delete<T>(endpoint: string, allowAnonymous: boolean = false): Observable<T> {
    return this.callWithAuth<T>(this.deleteFunc<T>(endpoint), allowAnonymous);
  }

  private deleteFunc<T>(endpoint: string) {
    return (auth) => {
      return this.httpClient.delete<T>(`/api/${endpoint}`, {
        headers: auth
      }).pipe(catchError(error => this.handleApiError(error)));
    }
  }

  private download(fileName: string, endpoint: string, payload: any, allowAnonymous: boolean = false): Observable<void> {
    return this.callWithAuth(this.downloadFunc(fileName, endpoint, payload), allowAnonymous);
  }

  private downloadFunc(fileName: string, endpoint: string, payload: any): (headers: HttpHeaders) => Observable<any> {
    return (auth) => {
      return this.httpClient.post(`/api/${endpoint}`, payload, {
        headers: auth,
        responseType: "blob"
      }).pipe(tap(response => {
        this.downloadFile(fileName, response);
      }));
    }
  }

  private downloadFile(fileName: string, file: Blob) {
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.download = fileName;
    a.href = URL.createObjectURL(file);
    a.target = '_blank';
    a.click();
    document.body.removeChild(a);
  }

  private callWithAuth<T>(call: (headers: HttpHeaders) => Observable<T>, allowAnonymous: boolean): Observable<T> {
    if (allowAnonymous) {
      return this.getAuthHeader().pipe(concatMap(call));
    }
    
    return this.checkIfTokenRefreshRequired(allowAnonymous)
      .pipe(
        // Auth0 handles token refresh automatically so we don't need to check
        concatMap(() => this.getAuthHeader()),
        concatMap(call)
      );
  }

  private logout() {
    // Clear any application-specific storage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    
    // Use Auth0's logout
    const returnTo = window.location.origin + '/gateway';
    
    this.auth0Service.logout({ 
      logoutParams: {
        returnTo: returnTo
      }
    });
  }
}
